import { useLayoutEffect, useState } from "react";
import { toast } from "react-toastify";

import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import { typedObjectEntries } from "common/helpers/utils";

import useAccountLimits from "../../datahooks/useAccountLimits";
import useAccountLimitsMutations from "../../datahooks/useAccountLimitsMutations";
import AccountLimits from "../AccountLimits";

import type { AccountLimits as AccountLimitsType } from "../../types";

interface AccountInteractionsProps {
  accountId: string;
  isRented: boolean;
}

export default function AccountInteractions({
  accountId,
  isRented,
}: AccountInteractionsProps) {
  const { permissions } = useSelectedWorkspaceContext();
  const { accountLimits, isLoadingAccountLimits } = useAccountLimits(accountId);
  const { setAccountLimits, isSettingAccountLimits } =
    useAccountLimitsMutations();

  const [updatedAccountLimits, setUpdatedAccountLimits] =
    useState<AccountLimitsType>();

  useLayoutEffect(() => {
    if (accountLimits) {
      setUpdatedAccountLimits(accountLimits);
    }
  }, [accountLimits]);

  function handleSave() {
    setAccountLimits({ accountId, limits: updatedAccountLimits }).catch(() =>
      toast.error("Error updating account limits"),
    );
  }

  const isChanged =
    accountLimits && updatedAccountLimits
      ? typedObjectEntries(updatedAccountLimits).some(([key, value]) => {
          if (key === "warmup") {
            return typedObjectEntries(updatedAccountLimits.warmup).some(
              ([warmupKey, warmupValue]) => {
                return warmupValue !== accountLimits.warmup[warmupKey];
              },
            );
          }
          return value !== accountLimits[key];
        })
      : false;

  const isViewOnly = isRented || !permissions.includes("accounts_full");

  return (
    <>
      <AccountLimits
        isLoading={isLoadingAccountLimits}
        accountLimits={updatedAccountLimits}
        setAccountLimits={setUpdatedAccountLimits}
        isViewOnly={isViewOnly}
      />

      <RenderIf condition={!isViewOnly}>
        <Button
          size="sm"
          variant="secondary-purple"
          className="ml-auto"
          onClick={handleSave}
          isLoading={isSettingAccountLimits}
          disabled={isLoadingAccountLimits || !isChanged}
        >
          Save
        </Button>
      </RenderIf>
    </>
  );
}
